import { Component, OnDestroy } from '@angular/core';
import { DateFormat, forLifeOf, LifecycleAware, Regex } from '@lsq-frontend/common-ui';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { ListRelationshipsForBulkEnrollmentInviteDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-enrollment-invite-dto';
import {
    BulkSendEnrollmentInviteEmailRequest,
    BulkSendEnrollmentInviteEmailService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/bulk-send-enrollment-invite-email';
import {
    ListRelationshipsForBulkActionRequest,
    ListRelationshipsForBulkEnrollmentInviteService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/list-relationships-for-bulk-enrollment-invite';
import { EnrollmentStatus } from '../../models';
import { AdminSuppliersBulkActionsService } from './bulk-actions.service';

@LifecycleAware()
@Component({
    selector: 'admin-supplier-bulk-enrollment-invite',
    template: `
        <bulk-action-layout
            [pageHeader]="'Send Enrollment Email'"
            [supplierCount]="(relationshipKeys$ | async).length"
            [confirmButtonText]="'Send Enrollment Emails'"
            [disableConfirm]="
                !checkableRowsService.checkedRows.length || notificationEmailsErrorMessage
            "
            (confirmClickHandler)="sendInvites()"
        >
            <div supplier-table>
                <lsq-data-table [dataSource]="dataSource" [isPaginated]="true" [layoutFixed]="true">
                    <lsq-checkbox-column
                        [checkableRowsService]="checkableRowsService"
                        [selectAllIncludesDisabled]="false"
                        [disableRow]="disableRow"
                        [column]="{
                            key: 'relationshipKey',
                            sortable: false,
                            width: '4rem'
                        }"
                    >
                    </lsq-checkbox-column>
                    <lsq-column
                        [column]="{
                            key: 'supplierName',
                            header: 'Supplier Name',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'buyerName',
                            header: 'Buyer Relationship',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [nullDataDash]="true"
                        [column]="{
                            key: 'primaryContactEmail',
                            header: 'Current Contact',
                            sortable: false,
                            truncateTextCss: true,
                            width: '15rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            header: 'Enrollment',
                            key: 'enrollmentStatus',
                            sortable: false,
                            width: '15rem'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div
                                class="enrollment-status"
                                *ngIf="
                                    !row.primaryContactEmail &&
                                    row.enrollmentStatus !== enrollmentStatus.INVITED &&
                                    row.enrollmentStatus !== enrollmentStatus.USER_CREATED &&
                                    row.enrollmentStatus !== enrollmentStatus.ENROLLED
                                "
                            >
                                <div class="enrollment-status-text">
                                    Not Ready
                                </div>
                            </div>
                            <div
                                class="enrollment-status"
                                *ngIf="
                                    row.primaryContactEmail &&
                                    row.enrollmentStatus !== enrollmentStatus.INVITED &&
                                    row.enrollmentStatus !== enrollmentStatus.USER_CREATED &&
                                    row.enrollmentStatus !== enrollmentStatus.ENROLLED
                                "
                            >
                                <div class="enrollment-status-text">
                                    Ready For Email
                                </div>
                            </div>
                            <div
                                class="enrollment-status"
                                *ngIf="row.enrollmentStatus === enrollmentStatus.INVITED"
                            >
                                <div class="enrollment-status-text">
                                    Sent {{ row.enrollmentStatusDate | daysPassed }}
                                </div>
                            </div>
                            <div
                                class="enrollment-status"
                                *ngIf="row.enrollmentStatus === enrollmentStatus.USER_CREATED"
                            >
                                <div class="enrollment-status-text">
                                    Enrollment in progress
                                </div>
                            </div>
                            <div
                                class="enrollment-status"
                                *ngIf="row.enrollmentStatus === enrollmentStatus.ENROLLED"
                            >
                                <div class="enrollment-status-text">
                                    Enrolled
                                    {{ row.enrollmentStatusDate | date: dateFormat.LsqShortYear }}
                                </div>
                            </div>
                        </ng-template>
                    </lsq-column>
                </lsq-data-table>
            </div>

            <div confirm-card>
                <p class="data-label">SUPPLIER ENROLLMENT</p>
                <p class="header" [style.marginBottom]="'2rem'">
                    Send Enrollment Email
                </p>
                <p class="body">
                    This action will send a new enrollment email to the contact listed for the
                    selected suppliers.
                </p>
                <br />
                <tag-input
                    [style.marginTop]="'3rem'"
                    [(ngModel)]="notificationEmails"
                    placeholder="john@company.com"
                    (ngModelChange)="validateEmails()"
                    label="Enrollment Notifications"
                    subLabel="Send notifications to other emails when the enrollment link has been sent out to these suppliers."
                    [tagIndexesWithError]="notificationEmailsWithError"
                    [errorMessage]="notificationEmailsErrorMessage"
                ></tag-input>
            </div>
        </bulk-action-layout>
    `
})
export class AdminSupplierBulkEnrollmentInviteComponent implements OnDestroy {
    public relationshipKeys$ = this._adminSuppliersBulkActionsService.relationshipKeys$;

    public checkableRowsService = this._adminSuppliersBulkActionsService.checkableRowsService;
    public disableRow = (row) =>
        !row.primaryContactEmail ||
        row.enrollmentStatus === EnrollmentStatus.ENROLLED ||
        row.enrollmentStatus === EnrollmentStatus.INELIGIBLE ||
        row.enrollmentStatus === EnrollmentStatus.UNKNOWN_ENROLLMENT_STATUS ||
        row.enrollmentStatus === EnrollmentStatus.USER_CREATED;

    public _skipSubject = new BehaviorSubject<number>(0);
    public _takeSubject = new BehaviorSubject<number>(10);
    public skip$ = this._skipSubject.asObservable();
    public take$ = this._takeSubject.asObservable();

    private _relationshipKeysToFetch$ = combineLatest([
        this.relationshipKeys$,
        this.skip$,
        this.take$
    ]).pipe(
        map(([relationshipKeys, skip, take]) =>
            relationshipKeys ? relationshipKeys.slice(skip, skip + take) : []
        )
    );

    // This is an unconventional data source, because we have not yet built out a backend that
    // keeps track of which rows we have checked, only the frontend knows. So we have to do manual
    // pagination by passing the relationshipKeys we want data for to the endpoint.
    public dataSource = new LsqListSource<ListRelationshipsForBulkEnrollmentInviteDto>({
        data$: this._listRelationshipsForBulkEnrollmentInvite.dataStream,
        rows$: this._listRelationshipsForBulkEnrollmentInvite.dataStream.pipe(pluck('rows')),
        error$: this._listRelationshipsForBulkEnrollmentInvite.errorStream,
        loading$: this._listRelationshipsForBulkEnrollmentInvite.isInFlightStream,
        totalCount$: this.relationshipKeys$.pipe(map((relationships) => relationships?.length)),
        skip: 0,
        take: 10,
        handleSkipChange: (skip) => {
            this.dataSource.skip = skip;
            this._skipSubject.next(skip);
        },
        handleTakeChange: (take) => {
            this.dataSource.take = take;
            this._takeSubject.next(take);
        }
    });

    public readonly dateFormat = DateFormat;
    public readonly enrollmentStatus = EnrollmentStatus;

    constructor(
        private _adminSuppliersBulkActionsService: AdminSuppliersBulkActionsService,
        private _listRelationshipsForBulkEnrollmentInvite: ListRelationshipsForBulkEnrollmentInviteService,
        private _bulkSendEnrollmentInviteEmail: BulkSendEnrollmentInviteEmailService
    ) {
        this._relationshipKeysToFetch$.pipe(forLifeOf(this)).subscribe((relationshipKeys) => {
            this._listRelationshipsForBulkEnrollmentInvite.execute(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys
                })
            );
            this.checkableRowsService.setCurrentPageKeys(relationshipKeys);
        });

        this._listRelationshipsForBulkEnrollmentInvite.dataStream
            .pipe(forLifeOf(this))
            .subscribe((response) => {
                response.rows.forEach((row) => {
                    if (this.disableRow(row)) {
                        this.checkableRowsService.uncheck(row.relationshipKey);
                    }
                });
            });
    }

    public notificationEmails: string[] = [];
    public notificationEmailsWithError: number[] = [];
    public notificationEmailsErrorMessage: string;

    public ngOnDestroy() {}

    public async sendInvites(): Promise<void> {
        this._adminSuppliersBulkActionsService.setIsSubmitting(true);
        const checkedRelationships = (
            await this._listRelationshipsForBulkEnrollmentInvite.executeOnce(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys: this.checkableRowsService.checkedRows as string[]
                })
            )
        ).rows;

        try {
            const response = await this._bulkSendEnrollmentInviteEmail.executeOnce(
                new BulkSendEnrollmentInviteEmailRequest({
                    relationshipKeys: checkedRelationships.map((rel) => rel.relationshipKey),
                    notificationEmails: this.notificationEmails
                })
            );

            this._adminSuppliersBulkActionsService.showConfirmationModal(
                `Enrollment Emails Sent`,
                `A new enrollment email has been sent to the suppliers listed below. Please note that the enrollment link for these emails will expire in 10 days.`,
                checkedRelationships,
                {
                    enrollmentNotificationEmails: this.notificationEmails
                }
            );
        } catch (error) {
            console.log(error);
        }
        this._adminSuppliersBulkActionsService.setIsSubmitting(false);
    }

    public validateEmails(): void {
        this.notificationEmailsWithError = [];
        const lowerCaseEmails = this.notificationEmails.map((email) => email.toLowerCase());

        // Email character length error takes precedence over format error.
        lowerCaseEmails.forEach((email, index) => {
            if (!Regex.Email.test(email)) {
                this.notificationEmailsWithError.push(index);
                this.notificationEmailsErrorMessage =
                    'Please make sure all emails are valid email addresses.';
            }
            if (email.length > 50) {
                if (!this.notificationEmailsWithError.includes(index)) {
                    this.notificationEmailsWithError.push(index);
                }
                this.notificationEmailsErrorMessage =
                    'Email addresses must be less than 50 characters long.';
            }
        });

        const duplicates = lowerCaseEmails.filter(
            (email, index) => lowerCaseEmails.indexOf(email) !== index
        );

        if (duplicates.length > 0) {
            duplicates.forEach((duplicate) => {
                this.notificationEmailsWithError.push(lowerCaseEmails.lastIndexOf(duplicate));
            });

            this.notificationEmailsErrorMessage = 'Can not have duplicated email addresses.';
        }

        if (this.notificationEmailsWithError.length === 0) {
            this.notificationEmailsErrorMessage = null;
        }

        // Having to many emails error takes precedence over all other errors.
        if (this.notificationEmails.length > 20) {
            this.notificationEmailsErrorMessage = 'Can not have more than 20 email addresses.';
        }
    }
}
